<template>
  <v-container
    id="match-properties"
    tag="section"
  >
    <div class="header">
      <h1>
        Match Properties
      </h1>
      <div class="header-subtext">
        <p>
          Complete your AutoResolve setup by matching NoiseAware and Breezeway properties. Once a property is matched, it will autosave and appear in the matched properties section below.
        </p>
        <v-btn
          class="icon-btn"
          color="muted"
          text
          href="https://help.noiseaware.io"
        >
          <v-icon size="12">
            $faQuestionCircle
          </v-icon>
          <span>Need Help?</span>
        </v-btn>
      </div>
    </div>
    <div id="property-matching-lists">
      <v-row justify="space-between">
        <v-col
          lg="5"
          md="6"
          sm="12"
        >
          <property-list
            :properties="filteredNoiseAwareProperties"
            :state="state"
            :selected.sync="match1"
            @search="noiseAwarePropertySearch = $event"
          />
        </v-col>
        <v-col
          lg="5"
          md="6"
          sm="12"
        >
          <!-- TODO: Dynamically add logo and partner name from state -->
          <property-list
            :properties="filteredPartnerProperties"
            :state="state"
            :selected.sync="match2"
            :title="'2. Select a Matching Property'"
            :logo="'breezeway-text.png'"
            :logo-alt-text="'Breezeway Logo'"
            @search="partnerPropertySearch = $event"
          />
        </v-col>
      </v-row>
    </div>
    <matched-properties
      :state.sync="state"
      :sort-selected.sync="sort"
      :newest-match.sync="newestMatch"
      :matched-properties="filteredMatchedProperties"
      @unmatch="handleUnmatch"
      @search-matched="matchedSearch = $event"
    />
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import PropertyList from '@/components/integrations/PropertyList.vue'
  import MatchedProperties from '@/components/integrations/MatchedProperties'
  import status from '@/helpers/property-matching-status'
  import axios from 'axios'

  export default {
    name: 'PropertyMatchingContainer',
    components: { PropertyList, MatchedProperties },
    data: () => ({
      state: status.LOADING,
      noiseawareData: [],
      partnerData: [],
      matchedListData: [],
      match1: null,
      match2: null,
      newestMatch: null,
      noiseAwarePropertySearch: '',
      partnerPropertySearch: '',
      matchedSearch: '',
      sort: { key: 'updated_at', order: 'ASC' },
    }),
    computed: {
      ...mapGetters({
        selectedOrg: 'Permissions/currentSelectedOrg',
        canAdminOrg: 'Permissions/canAdminOrg',
        isInternal: 'Permissions/isInternal',
      }),
      status () { return status },
      noiseAwareOrgId () { return this.$route.params.orgId },
      partnerName () { return this.$route.params.partnerName },
      filteredNoiseAwareProperties () {
        const formattedNoiseAwareProperties = this.noiseawareData.map(({ NoiseawareProperty, NoiseawarePropertyID }) => (
          this.formatProperty(NoiseawareProperty, NoiseawarePropertyID)
        ))
        formattedNoiseAwareProperties.sort((a, b) => { return a.name.localeCompare(b.name) })
        if (!this.noiseAwarePropertySearch) return formattedNoiseAwareProperties
        return formattedNoiseAwareProperties.filter(property => property.name.toLowerCase().includes(this.noiseAwarePropertySearch.toLowerCase()))
      },
      filteredPartnerProperties () {
        const formattedPartnerProperties = this.partnerData.map(({ PartnerProperty, PartnerPropertyID }) => (
          this.formatProperty(PartnerProperty, PartnerPropertyID)
        ))
        formattedPartnerProperties.sort((a, b) => { return a.name.localeCompare(b.name) })
        if (!this.partnerPropertySearch) return formattedPartnerProperties
        return formattedPartnerProperties.filter(property => property.name.toLowerCase().includes(this.partnerPropertySearch.toLowerCase()))
      },
      filteredMatchedProperties () {
        if (!this.matchedSearch) return this.formattedMatchedProperties
        return this.formattedMatchedProperties.filter(property => property.noiseaware.name.toLowerCase().includes(this.matchedSearch.toLowerCase()) || property.partner.name.toLowerCase().includes(this.matchedSearch.toLowerCase()))
      },
      formattedMatchedProperties () {
        return this.matchedListData.map((matchedProperties) => (
          this.formatMatchedProperties(matchedProperties)
        ))
      },
      noPropertiesAvailable () {
        return (this.noiseawareData.length === 0 && this.partnerData.length === 0 && this.matchedListData.length > 0)
      },
      allPropertiesMatched () {
        return (this.noiseawareData.length === 0 && this.partnerData.length === 0 && this.matchedListData.length > 0)
      },
      fetchAllPropertyMappingDataPayload () {
        return {
          MatchedPropertySort: {
            key: this.sort.key, // values: updated_at, noiseaware_name, partner_name
            order: this.sort.order, // values: ASC, DESC
          },
          NoiseawareOrganizationID: Number.parseInt(this.noiseAwareOrgId),
          noiseawarePayload: {
            filter: [
              {
                key: 'organization_id',
                operator: '=',
                value: this.noiseAwareOrgId,
              },
            ],
          },
          partnerName: this.partnerName,
          partnerPayload: {
            filter: [
              {
                key: 'partner_org_id',
                operator: '=',
                value: `${this.partnerOrgId}`,
              },
              {
                key: 'partner_id',
                operator: '=',
                value: `${this.partnerId}`,
              },
            ],
          },
        }
      },
      setPropertyMappingPayload () {
        return {
          MatchedPropertySort: {
            key: this.sort.key, // values: updated_at, noiseaware_name, partner_name
            order: this.sort.order, // values: ASC, DESC
          },
          MatchingNoiseAwarePartnerList: [
            {
              NoiseawareOrgID: parseInt(this.noiseAwareOrgId),
              NoiseawarePropertyID: this.match1,
              PartnerID: this.partnerId,
              PartnerName: this.partnerName,
              PartnerOrgID: this.partnerOrgId,
              PartnerPropertyID: this.match2,
            },
          ],
          PropertyMappingListPayload: {
            NoiseawareOrganizationID: parseInt(this.noiseAwareOrgId),
            noiseawarePayload: {
              filter: [
                {
                  key: 'organization_id',
                  operator: '=',
                  value: this.noiseAwareOrgId,
                },
              ],
            },
            partnerName: this.partnerName,
            partnerPayload: {
              filter: [
                {
                  key: 'partner_org_id',
                  operator: '=',
                  value: `${this.partnerOrgId}`,
                },
                {
                  key: 'partner_id',
                  operator: '=',
                  value: `${this.partnerId}`,
                },
              ],
            },
          },
        }
      },
    },
    watch: {
      match1 () {
        this.checkForMatch()
      },
      match2 () {
        this.checkForMatch()
      },
      sort () {
        this.fetchAllProperties()
      },
      async selectedOrg (newOrg) {
        const newId = newOrg.id
        if (!newId || !(this.isInternal || this.canAdminOrg(newId))) this.$router.push('/')
        else {
          this.$router.push(`/organizations/${newId}/integrations/${this.partnerName}`)
          await this.initializeProperties()
        }
      },
    },
    async created () {
      await this.initializeProperties()
    },
    methods: {
      async initializeProperties () {
        this.state = this.status.LOADING
        try {
          await this.getPartnerIdAndOrgId()
          await this.fetchAllProperties()
        } catch (e) {
          this.state = this.status.EMPTY
        }
      },
      async getPartnerIdAndOrgId () {
        const [infoRes, orgRelationRes] = await Promise.all([
          axios.get(`${this.$store.state.baseURL}/partner/partnerinfo/${this.partnerName}`),
          axios.get(`${this.$store.state.baseURL}/partner/orgpartnerrelation/${this.noiseAwareOrgId}/${this.partnerName}`),
        ])
        this.partnerId = infoRes?.data.data.PartnerID
        this.partnerOrgId = orgRelationRes?.data.data.CompanyRefIntID
      },
      async fetchAllProperties () {
        const res = await axios.post(`${this.$store.state.baseURL}/partner/listpropertymapping`, this.fetchAllPropertyMappingDataPayload)
        this.handlePropertyMappingResponse(res)
      },
      handlePropertyMappingResponse (res, isMatching) {
        const { NoiseawareNoMatch, PartnerNoMatch, NoiseawarePartnerMatch } = res.data.data
        this.noiseawareData = NoiseawareNoMatch || []
        this.partnerData = PartnerNoMatch || []
        if (isMatching) {
          this.newestMatch = this.formatMatchedProperties(NoiseawarePartnerMatch.find(item => item.NoiseawarePropertyID === this.setPropertyMappingPayload.MatchingNoiseAwarePartnerList[0].NoiseawarePropertyID))
          setTimeout(() => { this.matchedListData = NoiseawarePartnerMatch; this.newestMatch = null }, 1000)
        } else {
          this.matchedListData = NoiseawarePartnerMatch || []
        }
        this.isMatching = false

        if (this.allPropertiesMatched) this.state = this.status.MATCHED
        else if (this.noPropertiesAvailable) this.state = this.status.EMPTY
        else this.state = this.status.MATCHING
      },
      async checkForMatch () {
        if (this.match1 && this.match2) {
          const res = await axios.post(`${this.$store.state.baseURL}/partner/setpropertymapping`, this.setPropertyMappingPayload)
          this.handlePropertyMappingResponse(res, true)
          this.$store.dispatch('updateMessages', { type: 'success', text: 'Properties Matched Successfully' }, { root: true })
          this.match1 = null
          this.match2 = null
        }
      },
      async handleUnmatch ({ noiseaware, partner }) {
        const payload = {
          MatchedPropertySort: {
            key: this.sort.key, // values: updated_at, noiseaware_name, partner_name
            order: this.sort.order, // values: ASC, DESC
          },
          MatchingNoiseAwarePartnerList: [
            {
              NoiseawareOrgID: parseInt(this.noiseAwareOrgId),
              NoiseawarePropertyID: noiseaware.id,
              PartnerID: this.partnerId,
              PartnerName: this.partnerName,
              PartnerOrgID: this.partnerOrgId,
              PartnerPropertyID: null,
            },
          ],
          PropertyMappingListPayload: {
            NoiseawareOrganizationID: parseInt(this.noiseAwareOrgId),
            noiseawarePayload: {
              filter: [
                {
                  key: 'organization_id',
                  operator: '=',
                  value: this.noiseAwareOrgId,
                },
              ],
            },
            partnerName: this.partnerName,
            partnerPayload: {
              filter: [
                {
                  key: 'partner_org_id',
                  operator: '=',
                  value: `${this.partnerOrgId}`,
                },
                {
                  key: 'partner_id',
                  operator: '=',
                  value: `${this.partnerId}`,
                },
              ],
            },
          },
        }
        const res = await axios.post(`${this.$store.state.baseURL}/partner/setpropertymapping`, payload)
        this.handlePropertyMappingResponse(res)
        this.$store.dispatch('updateMessages', { type: 'success', text: 'Properties Unmatched Successfully' }, { root: true })
      },
      formatMatchedProperties ({ NoiseawareProperty, NoiseawarePropertyID, PartnerProperty, PartnerPropertyID }) {
        return {
          noiseaware: this.formatProperty(NoiseawareProperty, NoiseawarePropertyID),
          partner: this.formatProperty(PartnerProperty, PartnerPropertyID),
        }
      },
      formatProperty (property, id) {
        const { name, street1, street2, city, region, postalcode } = property
        return {
          name,
          id,
          address: this.formatAddress({ street1, street2, city, region, postalcode }),
        }
      },
      formatAddress ({ street1, street2, city, region, postalcode }) {
        if (street2) return `${street1} ${street2}, ${city}, ${region} ${postalcode}`
        return `${street1}, ${city}, ${region} ${postalcode}`
      },
    },
  }
</script>
<style lang="scss" scoped>
#match-properties {
  height: auto;
  padding: 0 32px;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  .header-subtext {
    p {
      width: 60%;
    }
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
}
#property-matching-lists {
  .col{
      max-width: 100%;
      min-width: 47%;
  }

}
</style>
